import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebase';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
    updatePassword,
    EmailAuthProvider,
    reauthenticateWithCredential
} from 'firebase/auth';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);

    async function fetchOrCreateUser(user, role = null) {
        try {
            const token = await user.getIdToken();
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };

            // Try by UID first
            let response = await fetch(`/api/users/${user.uid}`, { headers });

            if (response.ok) {
                const userData = await response.json();
                return userData.role;
            }

            // Try by email
            response = await fetch(`/api/users/email/${encodeURIComponent(user.email)}`, { headers });

            if (response.ok) {
                const userData = await response.json();
                return userData.role;
            }

            // User doesn't exist, create them
            // SECURITY FIX: Always use 'user' role for new users - never infer admin from email
            const newRole = 'user';

            await fetch('/api/users', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({
                    uid: user.uid,
                    email: user.email,
                    role: newRole
                })
            });

            return newRole;
        } catch (error) {
            console.error('Error in fetchOrCreateUser:', error);
            return 'user';
        }
    }

    async function signup(email, password, role = 'user') {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const token = await userCredential.user.getIdToken();

        // Create user in our backend
        // SECURITY: Backend will ignore the role and always set 'user'
        await fetch('/api/users', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                uid: userCredential.user.uid,
                email: email
                // Note: role is not sent - server always sets 'user' for new signups
            })
        });
        setUserRole('user');
        return userCredential;
    }

    // Helper function to get the current user's ID token for authenticated API requests
    async function getAuthToken() {
        if (auth.currentUser) {
            return await auth.currentUser.getIdToken();
        }
        return null;
    }

    async function login(email, password) {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const role = await fetchOrCreateUser(userCredential.user);
        setUserRole(role);
        return userCredential;
    }

    function logout() {
        setUserRole(null);
        return signOut(auth);
    }

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            if (user) {
                const role = await fetchOrCreateUser(user);
                setUserRole(role);
            } else {
                setUserRole(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    function resetPassword(email) {
        return sendPasswordResetEmail(auth, email);
    }

    async function updateUserPassword(currentPassword, newPassword) {
        if (!currentUser) {
            throw new Error('No user logged in');
        }

        // Re-authenticate user before changing password
        const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
        await reauthenticateWithCredential(currentUser, credential);

        // Update password
        return updatePassword(currentUser, newPassword);
    }

    async function deleteAccount(password) {
        if (!currentUser) {
            throw new Error('No user logged in');
        }

        // Re-authenticate user before deletion
        const credential = EmailAuthProvider.credential(currentUser.email, password);
        await reauthenticateWithCredential(currentUser, credential);

        // Delete user from backend
        const token = await currentUser.getIdToken();
        const response = await fetch(`/api/users/${currentUser.uid}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || 'Failed to delete account from server');
        }

        // Delete from Firebase Auth
        await currentUser.delete();

        setCurrentUser(null);
        setUserRole(null);
    }

    const value = {
        currentUser,
        userRole,
        isAdmin: userRole === 'admin',
        isLeader: userRole === 'leader',
        canManageEvents: userRole === 'admin' || userRole === 'leader',
        signup,
        login,
        logout,
        resetPassword,
        updateUserPassword,
        deleteAccount,
        getAuthToken
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}
